import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ConfirmDialog, ModuleHeader } from 'components/modals';
import type { ActionButton, ViewModeOption } from 'components/modals';
import {
  ActionMenu,
  type ActionMenuItem,
  SyutGrid,
  type SyutGridColumn,
  SyutSelect,
  SyutSearchGrid,
  MinimizedModalStrip,
} from 'components/ui';
import notificationService from '../../../../services/notificationService';
import { useModalMinimize } from 'contexts/ModalMinimizeContext';
import { useRoles, useDeleteRole, useCreateRole, useRoleKeycloakSync, useKeycloakClientRoles, useApplications } from 'hooks/api';
import type { RoleResponse, RoleExistenceValidation } from 'api/types';
import RoleFormModal from '../../../modals/modules/platform-admin/RoleFormModal';
import RoleViewModal from '../../../modals/modules/platform-admin/RoleViewModal';

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

type ViewMode = 'list' | 'grid' | 'calendar';
type RoleGridRow = RoleResponse & { id: string };

/** One row of the TenantHub ↔ Keycloak comparison shown in the sync dialog. */
interface RoleComparisonRow {
  /** Role code — the key both systems agree on. */
  code: string;
  name: string;
  description?: string;
  /** Present in the TenantHub database. */
  inHub: boolean;
  /** Present in the Keycloak client. */
  inKeycloak: boolean;
  /** The TenantHub role id, when it exists there (needed to push). */
  hubRoleId?: string;
}

interface ColumnDefinition {
  id: string;
  label: string;
  field: string;
  required: boolean;
  defaultVisible: boolean;
  width?: number;
  sortable?: boolean;
}

/** Which Keycloak operation the sync dialog is collecting inputs for. */
type KeycloakAction = 'sync-one' | 'sync-all' | 'import' | 'validate';

// ============================================================================
// COLUMN DEFINITIONS
// ============================================================================

const columnDefinitions: ColumnDefinition[] = [
  { id: 'select', label: '', field: 'select', required: true, defaultVisible: true, width: 50, sortable: false },
  { id: 'code', label: 'Code', field: 'code', required: false, defaultVisible: true, sortable: true },
  { id: 'name', label: 'Name', field: 'name', required: false, defaultVisible: true, sortable: true },
  { id: 'roleType', label: 'Type', field: 'roleType', required: false, defaultVisible: true, sortable: true },
  { id: 'application', label: 'Application', field: 'appId', required: false, defaultVisible: true, sortable: true },
  { id: 'description', label: 'Description', field: 'description', required: false, defaultVisible: true, sortable: true },
  { id: 'isComposite', label: 'Composite', field: 'isComposite', required: false, defaultVisible: true, sortable: true },
  { id: 'status', label: 'Status', field: 'statusCode', required: false, defaultVisible: true, sortable: true },
  { id: 'createdAt', label: 'Created', field: 'createdAt', required: false, defaultVisible: true, sortable: true },
  { id: 'updatedAt', label: 'Updated', field: 'updatedAt', required: false, defaultVisible: false, sortable: true },
  { id: 'actions', label: 'Actions', field: 'actions', required: true, defaultVisible: true, width: 80, sortable: false },
];

// ============================================================================
// COLUMN SELECTOR COMPONENT
// ============================================================================

interface ColumnSelectorProps {
  columns: ColumnDefinition[];
  visibleColumns: string[];
  onApply: (visibleColumns: string[]) => void;
  onReset: () => void;
}

const ColumnSelector: React.FC<ColumnSelectorProps> = ({
  columns,
  visibleColumns: initialVisible,
  onApply,
  onReset,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedColumns, setSelectedColumns] = useState<string[]>(initialVisible);
  const [isMobile, setIsMobile] = useState(false);
  const [confirmResetOpen, setConfirmResetOpen] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSelectedColumns(initialVisible);
      }
    };
    if (isOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, initialVisible]);

  const toggleColumn = (columnId: string) => {
    const column = columns.find(col => col.id === columnId);
    if (column?.required) return;
    setSelectedColumns(prev =>
      prev.includes(columnId) ? prev.filter(id => id !== columnId) : [...prev, columnId],
    );
  };

  const handleSelectAll = () => {
    const allColumnIds = columns.filter(col => !col.required).map(col => col.id);
    const requiredIds = columns.filter(col => col.required).map(col => col.id);
    if (selectedColumns.length === columns.length) setSelectedColumns(requiredIds);
    else setSelectedColumns([...requiredIds, ...allColumnIds]);
  };

  const handleApply = () => { onApply(selectedColumns); setIsOpen(false); };
  const handleCancel = () => { setSelectedColumns(initialVisible); setIsOpen(false); };

  return (
    <div style={{ position: 'relative', display: 'inline-block' }} ref={panelRef}>
      <button
        className={`btn-icon ${isOpen ? 'active' : ''}`}
        onClick={() => setIsOpen(!isOpen)}
        title="Manage Columns"
        style={isOpen ? { background: 'var(--accent-gradient)', color: 'white', borderColor: 'var(--primary)' } : {}}
      >
        <IconSystem.grid size={18} />
      </button>

      {isOpen && (
        <>
          {isMobile && (
            <div
              style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0, 0, 0, 0.5)', zIndex: 999 }}
              onClick={() => setIsOpen(false)}
            />
          )}
          <div style={{
            position: isMobile ? 'fixed' : 'absolute',
            top: isMobile ? '10%' : 'calc(100% + 0.5rem)',
            left: isMobile ? '50%' : 'auto',
            right: isMobile ? 'auto' : 0,
            transform: isMobile ? 'translateX(-50%)' : 'none',
            width: isMobile ? 'calc(100vw - 2rem)' : '320px',
            maxWidth: '320px',
            maxHeight: isMobile ? 'calc(90vh - 2rem)' : '500px',
            background: 'var(--bg-primary)',
            border: '2px solid var(--primary)',
            borderRadius: '0.75rem',
            boxShadow: '0 12px 32px rgba(0, 0, 0, 0.15)',
            zIndex: 1000,
            animation: 'slideDown 0.2s ease',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}>
            <div style={{
              padding: '1rem 1.25rem',
              borderBottom: '2px solid var(--border-color)',
              background: 'var(--bg-secondary)',
              borderRadius: '0.75rem 0.75rem 0 0',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexShrink: 0,
            }}>
              <h3 style={{ fontSize: '0.95rem', fontWeight: 600, color: 'var(--text-primary)', margin: 0 }}>
                Manage Columns
              </h3>
              <button
                onClick={() => setIsOpen(false)}
                style={{ width: '1.5rem', height: '1.5rem', border: 'none', background: 'transparent', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '0.25rem' }}
              >
                <IconSystem.x size={16} />
              </button>
            </div>

            <div style={{ flex: 1, overflowY: 'auto', padding: '0.75rem 0', minHeight: 0 }}>
              {columns.map(column => (
                <div
                  key={column.id}
                  onClick={() => toggleColumn(column.id)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '0.625rem 1.25rem',
                    cursor: column.required ? 'not-allowed' : 'pointer',
                    transition: 'all 0.2s ease',
                    gap: '0.75rem',
                    opacity: column.required ? 0.6 : 1,
                  }}
                >
                  <div style={{
                    width: '1.125rem',
                    height: '1.125rem',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: '2px solid var(--border-color)',
                    borderRadius: '0.25rem',
                    transition: 'all 0.2s ease',
                    flexShrink: 0,
                    ...(selectedColumns.includes(column.id) ? {
                      background: 'var(--accent-gradient)',
                      borderColor: 'var(--primary)',
                      color: 'white',
                    } : {}),
                  }}>
                    {selectedColumns.includes(column.id) && <IconSystem.check size={14} />}
                  </div>
                  <span style={{ flex: 1, fontSize: '0.85rem', color: 'var(--text-primary)', userSelect: 'none' }}>
                    {column.label}{column.required && ' *'}
                  </span>
                </div>
              ))}
            </div>

            <div style={{
              padding: '1rem',
              borderTop: '2px solid var(--border-color)',
              background: 'var(--bg-primary)',
              boxShadow: '0 -4px 12px rgba(0, 0, 0, 0.05)',
              display: 'flex',
              flexDirection: 'column',
              gap: '0.75rem',
              flexShrink: 0,
            }}>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button onClick={handleSelectAll} style={{ flex: 1, padding: '0.5rem 0.75rem', fontSize: '0.75rem', background: 'var(--bg-primary)', border: '1px solid var(--border-color)', borderRadius: '0.5rem', cursor: 'pointer' }}>
                  {selectedColumns.length === columns.length ? 'Deselect All' : 'Select All'}
                </button>
                <button onClick={() => setConfirmResetOpen(true)} style={{ flex: 1, padding: '0.5rem 0.75rem', fontSize: '0.75rem', background: 'var(--bg-primary)', border: '1px solid var(--border-color)', borderRadius: '0.5rem', cursor: 'pointer' }}>
                  Reset Default
                </button>
              </div>
              <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                <button onClick={handleCancel} className="btn btn-secondary">Cancel</button>
                <button onClick={handleApply} className="btn btn-primary">Apply</button>
              </div>
            </div>
          </div>
        </>
      )}

      <ConfirmDialog
        open={confirmResetOpen}
        title="Reset Columns"
        message="Reset to default column configuration?"
        confirmLabel="Reset"
        cancelLabel="Cancel"
        onConfirm={() => { onReset(); setConfirmResetOpen(false); setIsOpen(false); }}
        onCancel={() => setConfirmResetOpen(false)}
      />
    </div>
  );
};

// ============================================================================
// ROLE CARD COMPONENT (Grid View)
// ============================================================================

interface RoleCardProps {
  role: RoleGridRow;
  applicationLabel: string;
  onView: (role: RoleGridRow) => void;
  onEdit: (role: RoleGridRow) => void;
  onDelete: (role: RoleGridRow) => void;
}

const RoleCard: React.FC<RoleCardProps> = ({ role, applicationLabel, onView, onEdit, onDelete }) => {
  const menuItems: ActionMenuItem[] = useMemo(() => [
    { id: 'view', label: 'View Details', icon: <IconSystem.eye size={18} />, onClick: () => onView(role) },
    { id: 'edit', label: 'Edit', icon: <IconSystem.edit size={18} />, onClick: () => onEdit(role) },
    { id: 'delete', label: 'Delete', icon: <IconSystem.trash size={18} />, onClick: () => onDelete(role), variant: 'danger' },
  ], [role, onView, onEdit, onDelete]);

  return (
    <div
      style={{
        background: 'var(--bg-primary)',
        border: '1px solid var(--border-color)',
        borderRadius: '0.75rem',
        padding: '1.25rem',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
        transition: 'all 0.2s ease',
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        gap: '1rem',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'translateY(-2px)';
        e.currentTarget.style.boxShadow = '0 4px 12px rgba(37, 99, 235, 0.15)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.08)';
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '0.25rem' }}>
            {role.code}
          </div>
          <h3 style={{ fontSize: '1rem', fontWeight: 700, color: 'var(--text-primary)', margin: 0, marginBottom: '0.5rem' }}>
            {role.name}
          </h3>
        </div>
        <ActionMenu items={menuItems} />
      </div>

      <div style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '0.75rem',
        paddingTop: '0.75rem',
        borderTop: '1px solid var(--border-color)',
      }}>
        <div>
          <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>Type</div>
          <div style={{ fontSize: '0.85rem', fontWeight: 500, color: 'var(--text-primary)' }}>{role.roleType || '-'}</div>
        </div>
        <div>
          <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>Composite</div>
          <span className={`badge ${role.isComposite ? 'badge-active' : 'badge-inactive'}`}>
            {role.isComposite ? 'Yes' : 'No'}
          </span>
        </div>
      </div>

      <div style={{ paddingTop: '0.75rem', borderTop: '1px solid var(--border-color)', fontSize: '0.75rem', color: 'var(--text-muted)' }}>
        <div><strong>Application:</strong> {applicationLabel}</div>
        {role.createdAt && (
          <div style={{ marginTop: '0.25rem' }}>
            <strong>Created:</strong> {new Date(role.createdAt).toLocaleDateString()}
          </div>
        )}
      </div>
    </div>
  );
};

// ============================================================================
// MAIN ROLE MASTER SCREEN COMPONENT
// ============================================================================

interface RoleMasterScreenProps {
  moduleIcon?: string;
  tabId?: string;
  moduleName?: string;
}

const RoleMasterScreen: React.FC<RoleMasterScreenProps> = ({ moduleIcon, tabId, moduleName }) => {
  const { isMinimized, getMinimizedModal, dismiss } = useModalMinimize();
  const createInstanceCounter = useRef(0);
  const [createInstanceId, setCreateInstanceId] = useState<string>('');

  // ---- Server-side scoping: role type / application use dedicated endpoints ----
  const [serverRoleType, setServerRoleType] = useState<string>('');
  const [serverAppId, setServerAppId] = useState<string>('');

  // ---- API state ----
  const { data: roles, loading, refetch } = useRoles({
    roleType: serverRoleType || undefined,
    appId: serverAppId || undefined,
  });
  const { deleteRole, loading: deleting } = useDeleteRole();
  const { createRole, loading: creatingRole } = useCreateRole();
  const { syncToKeycloak, validateRole, loading: keycloakSyncBusy } = useRoleKeycloakSync();
  const keycloakBusy = keycloakSyncBusy || creatingRole;
  const { data: applications = [] } = useApplications();

  // Map roles to include the `id` field SyutGrid requires (already present on RoleResponse)
  const gridData: RoleGridRow[] = useMemo(
    () => roles.map(r => ({ ...r, id: String(r.id) })),
    [roles],
  );

  // Application id → readable label, used by the grid, cards and search popup
  const appLabelById = useMemo(() => {
    const map = new Map<string, string>();
    applications.forEach(a => map.set(String(a.appId), `${a.appCode} — ${a.appName}`));
    return map;
  }, [applications]);

  const applicationLabel = useCallback(
    (appId?: string | null) => (appId ? appLabelById.get(String(appId)) || String(appId) : '-'),
    [appLabelById],
  );

  // ---- Column visibility ----
  const [visibleColumns, setVisibleColumns] = useState<string[]>(
    () => columnDefinitions.filter(col => col.defaultVisible).map(col => col.id),
  );

  // ---- UI state ----
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [gridPage, setGridPage] = useState(0);
  const [gridPageSize, setGridPageSize] = useState(25);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterOpen, setFilterOpen] = useState(false);
  const [codeFilter, setCodeFilter] = useState('');
  const [nameFilter, setNameFilter] = useState('');
  const [descriptionFilter, setDescriptionFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [appFilter, setAppFilter] = useState('');
  const [compositeFilter, setCompositeFilter] = useState('');
  const [createdFromFilter, setCreatedFromFilter] = useState('');
  const [createdToFilter, setCreatedToFilter] = useState('');
  const [, setSelectedRows] = useState<RoleGridRow[]>([]);
  const [searchPopupOpen, setSearchPopupOpen] = useState(false);

  // ---- Modal state ----
  const [formModalOpen, setFormModalOpen] = useState(false);
  const [formMode, setFormMode] = useState<'create' | 'edit'>('create');
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedRole, setSelectedRole] = useState<RoleResponse | null>(null);

  // ---- Keycloak dialog state ----
  const [keycloakDialogOpen, setKeycloakDialogOpen] = useState(false);
  const [keycloakAction, setKeycloakAction] = useState<KeycloakAction>('sync-one');
  const [keycloakRealm, setKeycloakRealm] = useState('');
  const [keycloakClientId, setKeycloakClientId] = useState('');
  const [keycloakAppId, setKeycloakAppId] = useState('');
  const [keycloakErrors, setKeycloakErrors] = useState<Record<string, string>>({});
  const [validationResult, setValidationResult] = useState<RoleExistenceValidation | null>(null);
  /** Role codes ticked for transfer in the comparison list. */
  const [selectedSyncCodes, setSelectedSyncCodes] = useState<Set<string>>(new Set());
  /** Read inside the auto-select effect without making it depend on the flag. */
  const isBulkKeycloakActionRef = useRef(false);
  isBulkKeycloakActionRef.current = keycloakAction === 'sync-all' || keycloakAction === 'import';

  // ---- Load saved column preferences ----
  useEffect(() => {
    const saved = localStorage.getItem('role-master-columns');
    if (saved) {
      try { setVisibleColumns(JSON.parse(saved)); } catch { /* ignore */ }
    }
  }, []);

  // ---- F2 keyboard shortcut for search popup ----
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'F2' && !searchPopupOpen && !formModalOpen && !viewModalOpen && !keycloakDialogOpen) {
        e.preventDefault();
        setSearchPopupOpen(true);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [searchPopupOpen, formModalOpen, viewModalOpen, keycloakDialogOpen]);

  // ---- Filter options derived from loaded data (role_type is free-text server-side) ----
  const roleTypeOptions = useMemo(() => {
    const types = Array.from(new Set(roles.map(r => r.roleType).filter(Boolean))).sort();
    return [{ value: '', label: 'All Types' }, ...types.map(t => ({ value: t, label: t }))];
  }, [roles]);

  const applicationFilterOptions = useMemo(() => {
    const ids = Array.from(new Set(roles.map(r => (r.appId ? String(r.appId) : '')).filter(Boolean)));
    return [
      { value: '', label: 'All Applications' },
      ...ids.map(id => ({ value: id, label: appLabelById.get(id) || id })),
    ];
  }, [roles, appLabelById]);

  // ---- Filtered data ----
  const filteredData: RoleGridRow[] = useMemo(() => {
    let result = gridData;
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(r =>
        r.code.toLowerCase().includes(q) ||
        r.name.toLowerCase().includes(q) ||
        (r.description || '').toLowerCase().includes(q) ||
        (r.roleType || '').toLowerCase().includes(q),
      );
    }
    if (codeFilter.trim()) {
      const q = codeFilter.toLowerCase();
      result = result.filter(r => r.code.toLowerCase().includes(q));
    }
    if (nameFilter.trim()) {
      const q = nameFilter.toLowerCase();
      result = result.filter(r => r.name.toLowerCase().includes(q));
    }
    if (descriptionFilter.trim()) {
      const q = descriptionFilter.toLowerCase();
      result = result.filter(r => (r.description || '').toLowerCase().includes(q));
    }
    if (typeFilter) {
      result = result.filter(r => (r.roleType || '') === typeFilter);
    }
    if (appFilter) {
      result = result.filter(r => String(r.appId || '') === appFilter);
    }
    if (compositeFilter) {
      const wantComposite = compositeFilter === 'yes';
      result = result.filter(r => !!r.isComposite === wantComposite);
    }
    if (createdFromFilter) {
      result = result.filter(r => r.createdAt && r.createdAt >= createdFromFilter);
    }
    if (createdToFilter) {
      result = result.filter(r => r.createdAt && r.createdAt <= createdToFilter);
    }
    return result;
  }, [gridData, searchQuery, codeFilter, nameFilter, descriptionFilter, typeFilter, appFilter, compositeFilter, createdFromFilter, createdToFilter]);

  const clearAllFilters = useCallback(() => {
    setCodeFilter('');
    setNameFilter('');
    setDescriptionFilter('');
    setTypeFilter('');
    setAppFilter('');
    setCompositeFilter('');
    setCreatedFromFilter('');
    setCreatedToFilter('');
    setServerRoleType('');
    setServerAppId('');
  }, []);

  /**
   * Apply pushes the type/application selections to the dedicated server-side
   * endpoints (GET /Role/type/:t, GET /Role/app/:id); the rest stay client-side.
   */
  const applyFilters = useCallback(() => {
    // Only one server-side scope can apply at a time — application wins when both are set
    if (appFilter) {
      setServerAppId(appFilter);
      setServerRoleType('');
    } else if (typeFilter) {
      setServerRoleType(typeFilter);
      setServerAppId('');
    } else {
      setServerRoleType('');
      setServerAppId('');
    }
    setFilterOpen(false);
  }, [appFilter, typeFilter]);

  // ---- Column visibility handlers ----
  const handleApplyColumns = useCallback((newVisibleColumns: string[]) => {
    setVisibleColumns(newVisibleColumns);
    localStorage.setItem('role-master-columns', JSON.stringify(newVisibleColumns));
  }, []);

  const handleResetColumns = useCallback(() => {
    setVisibleColumns(columnDefinitions.filter(col => col.defaultVisible).map(col => col.id));
    localStorage.removeItem('role-master-columns');
  }, []);

  // ---- Handlers ----
  const handleView = useCallback((role: RoleResponse) => {
    const viewId = tabId ? `${tabId}-view-modal-${role.code}` : null;
    if (viewId && isMinimized(viewId)) {
      const modal = getMinimizedModal(viewId);
      dismiss(viewId);
      modal?.onRestore?.();
      return;
    }
    setSelectedRole(role);
    setViewModalOpen(true);
  }, [tabId, isMinimized, getMinimizedModal, dismiss]);

  const handleRestoreView = useCallback((roleCode: string) => {
    const role = roles.find(r => r.code === roleCode);
    if (role) {
      setSelectedRole(role);
      setViewModalOpen(true);
    }
  }, [roles]);

  const handleAddNew = useCallback(() => {
    const newId = String(++createInstanceCounter.current);
    setCreateInstanceId(newId);
    setSelectedRole(null);
    setFormMode('create');
    setFormModalOpen(true);
  }, []);

  const handleEdit = useCallback((role: RoleResponse) => {
    const modalId = tabId ? `${tabId}-form-modal-edit-${role.code}` : null;
    if (modalId && isMinimized(modalId)) {
      const modal = getMinimizedModal(modalId);
      dismiss(modalId);
      modal?.onRestore?.();
      return;
    }
    setSelectedRole(role);
    setFormMode('edit');
    setFormModalOpen(true);
  }, [tabId, isMinimized, getMinimizedModal, dismiss]);

  const handleDeleteClick = useCallback((role: RoleResponse) => {
    setSelectedRole(role);
    setDeleteModalOpen(true);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (!selectedRole) return;
    try {
      await deleteRole(selectedRole.id, {
        onSuccess: () => {
          setDeleteModalOpen(false);
          notificationService.toast.success(`Role "${selectedRole.name}" has been deleted.`);
          setSelectedRole(null);
          refetch();
        },
      });
    } catch (err: any) {
      setDeleteModalOpen(false);
      notificationService.toast.error(
        err?.response?.data?.message || err?.message || 'Failed to delete role. Please try again.',
      );
    }
  }, [selectedRole, deleteRole, refetch]);

  const handleFormSaveSuccess = useCallback(() => {
    setFormModalOpen(false);
    setSelectedRole(null);
    refetch();
  }, [refetch]);

  // ---- Keycloak dialog ----

  /**
   * Applies an application's stored Keycloak coordinates to the dialog:
   *   Client ID  = the application code (each application is registered in
   *                Keycloak under its code)
   *   Realm Name = effectiveRealmName resolved by the API (the app's own realm
   *                when dedicated, otherwise the platform shared realm)
   * Both remain editable so the administrator can target something else.
   */
  const applyApplicationDefaults = useCallback((appId: string) => {
    const app = applications.find(a => String(a.appId) === appId);
    setKeycloakAppId(appId);
    setKeycloakRealm(app ? app.effectiveRealmName || app.keycloakRealmName || '' : '');
    setKeycloakClientId(app ? app.appCode || '' : '');
    setKeycloakErrors({});
  }, [applications]);

  // Keycloak's own roles for the selected realm/client — loaded automatically
  // while the dialog is open, so the administrator always sees what is on the other side.
  const {
    data: keycloakRoles,
    loading: keycloakRolesLoading,
    message: keycloakRolesMessage,
    refetch: refetchKeycloakRoles,
  } = useKeycloakClientRoles(
    keycloakDialogOpen ? keycloakRealm : '',
    keycloakDialogOpen ? keycloakClientId : '',
  );

  /**
   * Full TenantHub role list, independent of the screen's filters — the grid may
   * be narrowed to one type/application, which would otherwise make roles look
   * "missing here" when they merely aren't in the current view.
   */
  const { data: allRoles, refetch: refetchAllRoles } = useRoles();

  /**
   * TenantHub ↔ Keycloak comparison for the selected application, keyed on role
   * code. Includes all three states: here only, Keycloak only, and in both.
   */
  const roleComparison: RoleComparisonRow[] = useMemo(() => {
    if (!keycloakDialogOpen) return [];

    const byCode = new Map<string, RoleComparisonRow>();

    // TenantHub side — roles belonging to the selected application
    allRoles
      .filter(r => keycloakAppId && String(r.appId || '') === keycloakAppId)
      .forEach(r => {
        const key = (r.code || '').toLowerCase();
        if (!key) return;
        byCode.set(key, {
          code: r.code,
          name: r.name || r.code,
          description: r.description,
          inHub: true,
          inKeycloak: false,
          hubRoleId: String(r.id),
        });
      });

    // Keycloak side — a code held anywhere in TenantHub counts as "in both",
    // because role codes are unique across the database.
    const hubByCode = new Map(allRoles.map(r => [(r.code || '').toLowerCase(), r]));
    (keycloakRoles ?? []).forEach(kr => {
      const key = (kr.name || '').toLowerCase();
      if (!key) return;
      const existing = byCode.get(key);
      if (existing) {
        existing.inKeycloak = true;
        if (!existing.description) existing.description = kr.description;
        return;
      }
      const hubRole = hubByCode.get(key);
      byCode.set(key, {
        code: kr.name,
        name: hubRole?.name || kr.name,
        description: kr.description || hubRole?.description,
        inHub: !!hubRole,
        inKeycloak: true,
        hubRoleId: hubRole ? String(hubRole.id) : undefined,
      });
    });

    // Missing-here first, then missing-in-Keycloak, then the in-sync ones
    const rank = (r: RoleComparisonRow) => (r.inHub && r.inKeycloak ? 2 : r.inKeycloak ? 0 : 1);
    return Array.from(byCode.values()).sort(
      (a, b) => rank(a) - rank(b) || a.code.localeCompare(b.code),
    );
  }, [keycloakDialogOpen, allRoles, keycloakRoles, keycloakAppId]);

  /** Rows the current direction can actually act on. */
  const isRowActionable = useCallback((row: RoleComparisonRow) => (
    keycloakAction === 'import'
      ? row.inKeycloak && !row.inHub   // pull: only what is missing here
      : row.inHub && !row.inKeycloak   // push: only what Keycloak lacks
  ), [keycloakAction]);

  // Pre-tick everything the chosen direction can transfer; the administrator can
  // untick individual rows. Re-runs when the direction or the comparison changes.
  useEffect(() => {
    if (!keycloakDialogOpen || !isBulkKeycloakActionRef.current) return;
    setSelectedSyncCodes(new Set(
      roleComparison.filter(isRowActionable).map(r => r.code),
    ));
  }, [keycloakDialogOpen, roleComparison, isRowActionable]);

  const toggleSyncSelection = useCallback((code: string) => {
    setSelectedSyncCodes(prev => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code);
      else next.add(code);
      return next;
    });
  }, []);

  const openKeycloakDialog = useCallback((action: KeycloakAction, role?: RoleResponse) => {
    setKeycloakAction(action);
    setKeycloakErrors({});
    setValidationResult(null);
    setSelectedSyncCodes(new Set());
    // Start from current data — the list may have changed since this screen loaded
    refetchAllRoles();
    if (role) setSelectedRole(role);
    // Row actions start from the role's own application; the bulk actions start
    // blank so the administrator picks the application to sync with.
    applyApplicationDefaults(role?.appId ? String(role.appId) : '');
    setKeycloakDialogOpen(true);
  }, [applyApplicationDefaults, refetchAllRoles]);

  const handleKeycloakSubmit = useCallback(async () => {
    const errors: Record<string, string> = {};
    if (!keycloakRealm.trim()) errors.realmName = 'Realm name is required';
    // validate() is the only operation where the API treats clientId as optional
    if (keycloakAction !== 'validate' && !keycloakClientId.trim()) {
      errors.clientId = 'Client ID is required';
    }
    if (Object.keys(errors).length > 0) {
      setKeycloakErrors(errors);
      return;
    }
    setKeycloakErrors({});

    try {
      if (keycloakAction === 'sync-one' && selectedRole) {
        await syncToKeycloak(selectedRole.id, keycloakRealm.trim(), keycloakClientId.trim());
        notificationService.toast.success(`Role "${selectedRole.name}" synced to Keycloak.`);
        setKeycloakDialogOpen(false);
        await Promise.all([refetch(), refetchAllRoles()]);
        refetchKeycloakRoles();
      } else if (keycloakAction === 'sync-all' || keycloakAction === 'import') {
        // Only the ticked rows move, one role at a time, so a single failure
        // does not abort the rest of the batch.
        const chosen = roleComparison.filter(r => selectedSyncCodes.has(r.code));
        if (chosen.length === 0) {
          setKeycloakErrors({ selection: 'Select at least one role to synchronise' });
          return;
        }

        let done = 0;
        const failures: string[] = [];

        for (const row of chosen) {
          try {
            if (keycloakAction === 'sync-all') {
              if (!row.hubRoleId) { failures.push(`${row.code} (not in TenantHub)`); continue; }
              await syncToKeycloak(row.hubRoleId, keycloakRealm.trim(), keycloakClientId.trim());
            } else {
              // Pull: create the TenantHub record only — Keycloak already has it
              await createRole({
                code: row.code,
                name: row.name || row.code,
                roleType: 'custom',
                appId: keycloakAppId || undefined,
                description: row.description,
                createInKeycloak: false,
              });
            }
            done++;
          } catch (err: any) {
            failures.push(`${row.code} (${err?.response?.data?.message || err?.message || 'failed'})`);
          }
        }

        // Reload BOTH sides before anything is rendered again: the grid, the
        // unfiltered list backing the comparison, and Keycloak's own roles.
        // Without all three the comparison keeps showing pre-sync state.
        setSelectedSyncCodes(new Set());
        await Promise.all([refetch(), refetchAllRoles()]);
        refetchKeycloakRoles();

        if (failures.length === 0) {
          notificationService.toast.success(
            `${done} role(s) ${keycloakAction === 'sync-all' ? 'pushed to Keycloak' : 'pulled into TenantHub'}.`,
          );
        } else {
          notificationService.toast.warning(
            `${done} of ${chosen.length} role(s) synchronised. Failed: ${failures.slice(0, 3).join('; ')}` +
            (failures.length > 3 ? ` and ${failures.length - 3} more.` : ''),
            8000,
          );
        }
      } else if (keycloakAction === 'validate' && selectedRole) {
        await validateRole(selectedRole.code, keycloakRealm.trim(), keycloakClientId.trim() || undefined, {
          onSuccess: (result) => {
            setValidationResult(result ?? null);
          },
        });
      }
    } catch (err: any) {
      notificationService.toast.error(
        err?.response?.data?.message || err?.message || 'Keycloak operation failed. Please try again.',
      );
    }
  }, [keycloakAction, keycloakRealm, keycloakClientId, keycloakAppId, selectedRole, roleComparison, selectedSyncCodes, syncToKeycloak, createRole, validateRole, refetch, refetchAllRoles, refetchKeycloakRoles]);

  // ---- Grid columns ----
  const tableColumns: SyutGridColumn<RoleGridRow>[] = useMemo(() => [
    { id: 'code', header: 'Code', accessorKey: 'code', size: 160 },
    { id: 'name', header: 'Name', accessorKey: 'name', size: 200 },
    {
      id: 'roleType',
      header: 'Type',
      accessorKey: 'roleType',
      size: 120,
      cell: ({ getValue }) => (getValue() as string) || '-',
    },
    {
      id: 'application',
      header: 'Application',
      accessorKey: 'appId',
      size: 200,
      cell: ({ getValue }) => applicationLabel(getValue() as string | null),
    },
    {
      id: 'description',
      header: 'Description',
      accessorKey: 'description',
      cell: ({ getValue }) => (getValue() as string) || '-',
    },
    {
      id: 'isComposite',
      header: 'Composite',
      accessorKey: 'isComposite',
      size: 110,
      cell: ({ getValue }) => (
        <span className={`badge ${getValue() ? 'badge-active' : 'badge-inactive'}`}>
          {getValue() ? 'Yes' : 'No'}
        </span>
      ),
    },
    {
      id: 'status',
      header: 'Status',
      accessorKey: 'statusCode',
      size: 110,
      cell: ({ row }) => {
        const code = (row.original.statusCode || '').toLowerCase();
        if (!code) return '-';
        const isInactive = code.includes('inactive');
        return (
          <span className={`badge ${isInactive ? 'badge-inactive' : 'badge-active'}`}>
            {row.original.statusName || (isInactive ? 'Inactive' : 'Active')}
          </span>
        );
      },
    },
    {
      id: 'createdAt',
      header: 'Created',
      accessorKey: 'createdAt',
      size: 130,
      cell: ({ getValue }) => {
        const val = getValue() as string;
        return val ? new Date(val).toLocaleDateString() : '-';
      },
    },
    {
      id: 'updatedAt',
      header: 'Updated',
      accessorKey: 'updatedAt',
      size: 130,
      cell: ({ getValue }) => {
        const val = getValue() as string;
        return val ? new Date(val).toLocaleDateString() : '-';
      },
    },
  ], [applicationLabel]);

  // ---- Row actions ----
  const getRowActions = useCallback((role: RoleGridRow): ActionMenuItem[] => [
    { id: 'view', label: 'View Details', icon: <IconSystem.eye size={18} />, onClick: () => handleView(role) },
    { id: 'edit', label: 'Edit', icon: <IconSystem.edit size={18} />, onClick: () => handleEdit(role) },
    { id: 'sync', label: 'Sync to Keycloak', icon: <IconSystem.refresh size={18} />, onClick: () => openKeycloakDialog('sync-one', role) },
    { id: 'validate', label: 'Validate in Keycloak', icon: <IconSystem.check size={18} />, onClick: () => openKeycloakDialog('validate', role) },
    { id: 'delete', label: 'Delete', icon: <IconSystem.trash size={18} />, onClick: () => handleDeleteClick(role), variant: 'danger' },
  ], [handleView, handleEdit, handleDeleteClick, openKeycloakDialog]);

  // ---- View mode options ----
  const viewModeOptions: ViewModeOption[] = [
    { id: 'list', icon: <IconSystem.list size={16} />, label: 'List View', tooltip: 'List View' },
    { id: 'grid', icon: <IconSystem.grid size={16} />, label: 'Grid View', tooltip: 'Grid View' },
    { id: 'calendar', icon: <IconSystem.calendar size={16} />, label: 'Calendar View', tooltip: 'Calendar View (Not Applicable)', disabled: true },
  ];

  // ---- Action buttons ----
  const actionButtons: ActionButton[] = [
    { id: 'search', icon: <IconSystem.search size={18} />, label: 'Search Roles', onClick: () => setSearchPopupOpen(true), tooltip: 'Search Roles (F2)', variant: 'icon', visible: true },
    { id: 'add', icon: <IconSystem.add size={18} />, label: 'Add Role', onClick: handleAddNew, tooltip: 'Create New Role', variant: 'icon', visible: true },
    // Single dedicated entry point for all Keycloak synchronisation (both directions)
    { id: 'sync-keycloak', icon: <IconSystem.link size={18} />, label: 'Sync Keycloak', onClick: () => openKeycloakDialog('sync-all'), tooltip: 'Synchronise roles with Keycloak', variant: 'icon' },
    // Import is data import (file-based) and is deliberately NOT Keycloak sync
    { id: 'import', icon: <IconSystem.download size={18} />, label: 'Import', onClick: () => notificationService.toast.info('Role file import is not available yet.'), tooltip: 'Import roles from file', variant: 'icon' },
    { id: 'refresh', icon: <IconSystem.refresh size={18} />, label: 'Refresh', onClick: refetch, tooltip: 'Refresh', variant: 'icon' },
  ];

  /** True when the dialog was opened from the toolbar (bulk, direction-selectable). */
  const isBulkKeycloakAction = keycloakAction === 'sync-all' || keycloakAction === 'import';

  const keycloakDialogTitle =
    keycloakAction === 'sync-one' ? `Sync "${selectedRole?.code || ''}" to Keycloak`
      : isBulkKeycloakAction ? 'Sync Keycloak'
        : `Validate "${selectedRole?.code || ''}" in Keycloak`;

  return (
    <div className="module-master-container">
      <ModuleHeader
        visible={true}
        title="Roles"
        description="Manage system, tenant and application roles"
        icon={moduleIcon && (IconSystem as any)[moduleIcon]
          ? React.createElement((IconSystem as any)[moduleIcon], { size: 18 })
          : <IconSystem.lock size={18} />}
        searchConfig={{
          value: searchQuery,
          onChange: setSearchQuery,
          placeholder: 'Search roles...',
        }}
        viewModeConfig={{
          currentMode: viewMode,
          modes: viewModeOptions,
          onChange: (mode) => setViewMode(mode as ViewMode),
        }}
        filterConfig={{
          onClick: () => setFilterOpen(!filterOpen),
          isActive: filterOpen,
        }}
        leadingToolbarItems={tabId ? <MinimizedModalStrip tabId={tabId} /> : undefined}
        customToolbarItems={
          <ColumnSelector
            columns={columnDefinitions}
            visibleColumns={visibleColumns}
            onApply={handleApplyColumns}
            onReset={handleResetColumns}
          />
        }
        actions={actionButtons}
      />

      <div className="module-content" style={{ marginTop: '6px' }}>
        <div className="data-grid-container">
          {viewMode === 'list' && (
            <SyutGrid<RoleGridRow>
              data={filteredData}
              columns={tableColumns}
              enableRowSelection
              enablePagination
              rowActions={getRowActions}
              onSelectionChange={setSelectedRows}
              emptyMessage={loading ? 'Loading roles...' : 'No roles found'}
              visibleColumns={visibleColumns}
            />
          )}

          {viewMode === 'grid' && (() => {
            const totalPages = Math.max(1, Math.ceil(filteredData.length / gridPageSize));
            const safeGridPage = Math.min(gridPage, totalPages - 1);
            const pagedData = filteredData.slice(safeGridPage * gridPageSize, (safeGridPage + 1) * gridPageSize);
            return (
              <div className="syut-grid-component" style={{ height: '100%', overflow: 'auto' }}>
                <div style={{
                  padding: '1rem',
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))',
                  gap: '1.5rem',
                }}>
                  {filteredData.length === 0 ? (
                    <div style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '3rem', color: 'var(--text-muted)' }}>
                      <IconSystem.lock size={48} style={{ marginBottom: '1rem', opacity: 0.5 }} />
                      <p>{loading ? 'Loading roles...' : 'No roles found'}</p>
                    </div>
                  ) : (
                    pagedData.map(role => (
                      <RoleCard
                        key={role.id}
                        role={role}
                        applicationLabel={applicationLabel(role.appId as string | null)}
                        onView={handleView}
                        onEdit={handleEdit}
                        onDelete={handleDeleteClick}
                      />
                    ))
                  )}
                </div>
                {filteredData.length > 0 && (
                  <div className="pagination-footer" style={{ position: 'sticky', bottom: 0 }}>
                    <div className="pagination-info">
                      Showing {pagedData.length} of {filteredData.length} items
                    </div>
                    <div className="pagination-controls" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <select
                        value={gridPageSize}
                        onChange={(e) => { setGridPageSize(Number(e.target.value)); setGridPage(0); }}
                        style={{ padding: '0.375rem 0.5rem', borderRadius: '0.375rem', border: '1px solid var(--border-color)', fontSize: '0.85rem', marginRight: '1rem' }}
                      >
                        {[10, 25, 50, 100].map((size) => (
                          <option key={size} value={size}>{size} / page</option>
                        ))}
                      </select>
                      <button className="page-btn" onClick={() => setGridPage(0)} disabled={safeGridPage === 0}>
                        <IconSystem.chevronLeft size={16} /><IconSystem.chevronLeft size={16} style={{ marginLeft: '-8px' }} />
                      </button>
                      <button className="page-btn" onClick={() => setGridPage(safeGridPage - 1)} disabled={safeGridPage === 0}>
                        <IconSystem.chevronLeft size={16} />
                      </button>
                      <span style={{ padding: '0 0.75rem', fontSize: '0.85rem' }}>
                        Page {safeGridPage + 1} of {totalPages}
                      </span>
                      <button className="page-btn" onClick={() => setGridPage(safeGridPage + 1)} disabled={safeGridPage >= totalPages - 1}>
                        <IconSystem.chevronRight size={16} />
                      </button>
                      <button className="page-btn" onClick={() => setGridPage(totalPages - 1)} disabled={safeGridPage >= totalPages - 1}>
                        <IconSystem.chevronRight size={16} /><IconSystem.chevronRight size={16} style={{ marginLeft: '-8px' }} />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })()}

          {viewMode === 'calendar' && (
            <div style={{ padding: '3rem', textAlign: 'center', color: 'var(--text-muted)' }}>
              <IconSystem.calendar size={48} style={{ marginBottom: '1rem', opacity: 0.3 }} />
              <h3 style={{ color: 'var(--text-secondary)', marginBottom: '0.5rem' }}>Calendar View Not Applicable</h3>
              <p>Calendar view is not available for Roles</p>
            </div>
          )}
        </div>

        {filterOpen && (
          <div className="search-panel">
            <div className="search-panel-header">
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
                <IconSystem.filter size={14} />
                <h3 className="search-panel-title">Filters</h3>
              </div>
              <button
                onClick={() => { clearAllFilters(); setFilterOpen(false); }}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '0.25rem',
                  display: 'flex',
                  alignItems: 'center',
                  color: 'var(--text-muted)',
                  borderRadius: '0.25rem',
                }}
                title="Close filters"
              >
                <IconSystem.x size={16} />
              </button>
            </div>

            <div className="search-panel-content">
              <div className="filter-section">
                <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>Code</label>
                <input
                  type="text"
                  className="form-input"
                  value={codeFilter}
                  onChange={(e) => setCodeFilter(e.target.value)}
                  placeholder="Filter by code..."
                  style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }}
                />
              </div>

              <div className="filter-section">
                <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>Name</label>
                <input
                  type="text"
                  className="form-input"
                  value={nameFilter}
                  onChange={(e) => setNameFilter(e.target.value)}
                  placeholder="Filter by name..."
                  style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }}
                />
              </div>

              <div className="filter-section">
                <SyutSelect
                  label="Role Type"
                  options={roleTypeOptions}
                  value={typeFilter}
                  onChange={(val: string | string[] | null) => setTypeFilter((val as string) || '')}
                  placeholder="Select type..."
                  isClearable={false}
                  size="sm"
                />
              </div>

              <div className="filter-section">
                <SyutSelect
                  label="Application"
                  options={applicationFilterOptions}
                  value={appFilter}
                  onChange={(val: string | string[] | null) => setAppFilter((val as string) || '')}
                  placeholder="Select application..."
                  isClearable={false}
                  size="sm"
                />
              </div>

              <div className="filter-section">
                <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>Description</label>
                <input
                  type="text"
                  className="form-input"
                  value={descriptionFilter}
                  onChange={(e) => setDescriptionFilter(e.target.value)}
                  placeholder="Filter by description..."
                  style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }}
                />
              </div>

              <div className="filter-section">
                <SyutSelect
                  label="Composite"
                  options={[
                    { value: '', label: 'All' },
                    { value: 'yes', label: 'Yes' },
                    { value: 'no', label: 'No' },
                  ]}
                  value={compositeFilter}
                  onChange={(val: string | string[] | null) => setCompositeFilter((val as string) || '')}
                  placeholder="Composite?"
                  isClearable={false}
                  size="sm"
                />
              </div>

              <div className="filter-section">
                <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>Created From</label>
                <input
                  type="date"
                  className="form-input"
                  value={createdFromFilter}
                  onChange={(e) => setCreatedFromFilter(e.target.value)}
                  style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }}
                />
              </div>

              <div className="filter-section">
                <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>Created To</label>
                <input
                  type="date"
                  className="form-input"
                  value={createdToFilter}
                  onChange={(e) => setCreatedToFilter(e.target.value)}
                  style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }}
                />
              </div>
            </div>

            <div className="filter-actions">
              <button className="btn btn-secondary" onClick={clearAllFilters}>Clear</button>
              <button className="btn btn-primary" onClick={applyFilters}>Apply</button>
            </div>
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      <RoleFormModal
        isOpen={formModalOpen}
        mode={formMode}
        role={formMode === 'edit' ? selectedRole : null}
        onClose={() => { setFormModalOpen(false); setSelectedRole(null); }}
        onMinimize={() => setFormModalOpen(false)}
        onRestoreModal={(restoredMode, instanceId, roleCode) => {
          if (instanceId) setCreateInstanceId(instanceId);
          if (roleCode) {
            const r = roles.find(x => x.code === roleCode);
            if (r) setSelectedRole(r);
          }
          setFormMode(restoredMode);
          setFormModalOpen(true);
        }}
        onSaveSuccess={handleFormSaveSuccess}
        tabId={tabId}
        moduleName={moduleName}
        createInstanceId={createInstanceId}
      />

      {/* View Modal */}
      <RoleViewModal
        isOpen={viewModalOpen}
        role={selectedRole}
        onClose={() => { setViewModalOpen(false); setSelectedRole(null); }}
        tabId={tabId}
        moduleName={moduleName}
        onRestoreView={handleRestoreView}
      />

      {/* Delete Confirmation — the API performs a hard delete */}
      <ConfirmDialog
        open={deleteModalOpen}
        title="Delete Role"
        message={`Permanently delete role "${selectedRole?.name}" (${selectedRole?.code})? The role is removed from the database and cannot be restored. Users currently holding it will lose the associated permissions.`}
        confirmLabel="Delete"
        cancelLabel="Cancel"
        variant="danger"
        loading={deleting}
        loadingMessage="Deleting..."
        onConfirm={handleConfirmDelete}
        onCancel={() => { setDeleteModalOpen(false); setSelectedRole(null); }}
      />

      {/* Keycloak sync / import / validate dialog */}
      {keycloakDialogOpen && (
        <>
          <div className="modal-backdrop" onClick={() => setKeycloakDialogOpen(false)} />
          <div className="modal-container modal-md">
            {keycloakBusy && (
              <div style={{
                position: 'absolute', inset: 0, background: 'var(--modal-loading-overlay)',
                display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                zIndex: 10, borderRadius: 'inherit',
              }}>
                <div style={{
                  width: '2.5rem', height: '2.5rem', border: '3px solid var(--border-color)',
                  borderTopColor: 'var(--primary)', borderRadius: '50%', animation: 'spin 0.8s linear infinite',
                }} />
                <p style={{ marginTop: '1rem', fontSize: '0.875rem', color: 'var(--text-secondary)', fontWeight: 500 }}>
                  Contacting Keycloak...
                </p>
                <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
              </div>
            )}

            <div className="modal-header" style={{ minHeight: 'auto' }}>
              <h2 className="modal-title">
                <IconSystem.link size={20} />
                {keycloakDialogTitle}
              </h2>
              <button className="modal-close" onClick={() => setKeycloakDialogOpen(false)} disabled={keycloakBusy}>
                <IconSystem.x size={20} />
              </button>
            </div>

            <div className="modal-content">
              {/* Direction — only for the toolbar (bulk) entry point. Row actions
                  already know what they are doing. */}
              {isBulkKeycloakAction && (
                <div className="form-group" data-field="direction">
                  <SyutSelect
                    label="Direction"
                    options={[
                      { value: 'sync-all', label: 'Push — send TenantHub roles to Keycloak' },
                      { value: 'import', label: 'Pull — bring Keycloak roles into TenantHub' },
                    ]}
                    value={keycloakAction}
                    onChange={(val: string | string[] | null) => {
                      setKeycloakAction(((val as string) || 'sync-all') as KeycloakAction);
                      setKeycloakErrors({});
                    }}
                    placeholder="Select direction"
                    isClearable={false}
                  />
                </div>
              )}

              {/* Application drives the realm/client below — for a row action it
                  arrives pre-selected from the role's own application. */}
              <div className="form-group" data-field="application">
                <SyutSelect
                  label="Application"
                  options={[
                    { value: '', label: 'None — enter realm and client manually' },
                    ...applications.map(a => ({ value: String(a.appId), label: `${a.appCode} — ${a.appName}` })),
                  ]}
                  value={keycloakAppId}
                  onChange={(val: string | string[] | null) => applyApplicationDefaults((val as string) || '')}
                  placeholder="Select application"
                  isClearable={false}
                />
                <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginTop: '0.25rem' }}>
                  {keycloakAction === 'import'
                    ? 'Sets the realm and client to pull from, and links the pulled roles to this application.'
                    : 'Fills in the realm and client registered for this application.'}
                </div>
              </div>

              <div className="form-grid-2">
                <div className="form-group" data-field="realmName" style={{ marginBottom: 0 }}>
                  <label className="form-label">Realm Name <span className="text-danger">*</span></label>
                  <input
                    type="text"
                    className={`form-input ${keycloakErrors.realmName ? 'form-field-error' : ''}`}
                    value={keycloakRealm}
                    onChange={(e) => { setKeycloakRealm(e.target.value); setKeycloakErrors(p => ({ ...p, realmName: '' })); }}
                    placeholder="Select an application to fill this in"
                  />
                  {keycloakErrors.realmName && <div className="form-error-message">{keycloakErrors.realmName}</div>}
                </div>

                <div className="form-group" data-field="clientId" style={{ marginBottom: 0 }}>
                  <label className="form-label">
                    Client ID {keycloakAction !== 'validate' && <span className="text-danger">*</span>}
                  </label>
                  <input
                    type="text"
                    className={`form-input ${keycloakErrors.clientId ? 'form-field-error' : ''}`}
                    value={keycloakClientId}
                    onChange={(e) => { setKeycloakClientId(e.target.value); setKeycloakErrors(p => ({ ...p, clientId: '' })); }}
                    placeholder="Select an application to fill this in"
                  />
                  {keycloakErrors.clientId && <div className="form-error-message">{keycloakErrors.clientId}</div>}
                  {keycloakAction === 'validate' && (
                    <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginTop: '0.25rem' }}>
                      Leave blank to check for a realm role instead of a client role.
                    </div>
                  )}
                </div>
              </div>

              {/* Full TenantHub ↔ Keycloak comparison for the selected application.
                  Loads by itself once an application is chosen and lists all three
                  states. Tick the rows to transfer — the direction decides which
                  rows can be ticked. */}
              {(keycloakRealm.trim() && keycloakClientId.trim()) && (
                <div style={{ marginTop: '0.75rem' }} data-field="selection">
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: '0.5rem',
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    color: 'var(--text-secondary)',
                    marginBottom: '0.375rem',
                  }}>
                    <span>Role comparison — client "{keycloakClientId}" ({keycloakRealm})</span>
                    {isBulkKeycloakAction && roleComparison.some(isRowActionable) && (
                      <button
                        className="btn btn-secondary"
                        style={{ padding: '0.125rem 0.5rem', fontSize: '0.7rem' }}
                        onClick={() => {
                          const actionable = roleComparison.filter(isRowActionable).map(r => r.code);
                          setSelectedSyncCodes(prev =>
                            actionable.every(c => prev.has(c)) ? new Set() : new Set(actionable),
                          );
                        }}
                      >
                        {roleComparison.filter(isRowActionable).every(r => selectedSyncCodes.has(r.code))
                          ? 'Clear all' : 'Select all'}
                      </button>
                    )}
                  </div>

                  {keycloakRolesLoading ? (
                    <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', padding: '0.5rem 0' }}>
                      Reading roles from Keycloak...
                    </div>
                  ) : roleComparison.length === 0 ? (
                    <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', padding: '0.5rem 0' }}>
                      {keycloakRolesMessage || 'No roles found in TenantHub or Keycloak for this application.'}
                    </div>
                  ) : (
                    <>
                      <div style={{
                        border: '1px solid var(--border-color)',
                        borderRadius: '6px',
                        maxHeight: '260px',
                        overflowY: 'auto',
                      }}>
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.5rem',
                          padding: '0.5rem 0.75rem',
                          background: 'var(--bg-secondary)',
                          fontSize: '0.7rem',
                          fontWeight: 600,
                          color: 'var(--text-secondary)',
                          position: 'sticky',
                          top: 0,
                          textTransform: 'uppercase',
                          letterSpacing: '0.03em',
                        }}>
                          <span style={{ width: '1.25rem', flexShrink: 0 }} />
                          <span style={{ flex: 1 }}>Role</span>
                          <span style={{ width: '110px', textAlign: 'center', flexShrink: 0 }}>TenantHub</span>
                          <span style={{ width: '110px', textAlign: 'center', flexShrink: 0 }}>Keycloak</span>
                        </div>

                        {roleComparison.map(row => {
                          const actionable = isBulkKeycloakAction && isRowActionable(row);
                          const checked = selectedSyncCodes.has(row.code);
                          const inBoth = row.inHub && row.inKeycloak;
                          return (
                            <div
                              key={row.code}
                              onClick={() => actionable && toggleSyncSelection(row.code)}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.5rem',
                                padding: '0.375rem 0.75rem',
                                borderTop: '1px solid var(--border-color)',
                                fontSize: '0.8rem',
                                cursor: actionable ? 'pointer' : 'default',
                                opacity: actionable || inBoth ? 1 : 0.65,
                              }}
                            >
                              <input
                                type="checkbox"
                                checked={checked}
                                disabled={!actionable}
                                onChange={() => toggleSyncSelection(row.code)}
                                onClick={(e) => e.stopPropagation()}
                                title={actionable
                                  ? 'Include this role in the synchronisation'
                                  : inBoth
                                    ? 'Already present in both systems'
                                    : keycloakAction === 'import'
                                      ? 'Only in TenantHub — switch direction to Push to transfer it'
                                      : 'Only in Keycloak — switch direction to Pull to transfer it'}
                                style={{ width: '1.25rem', flexShrink: 0, cursor: actionable ? 'pointer' : 'not-allowed' }}
                              />
                              <span style={{ flex: 1, minWidth: 0 }}>
                                <strong>{row.code}</strong>
                                {row.description ? (
                                  <span style={{ color: 'var(--text-muted)' }}> — {row.description}</span>
                                ) : null}
                              </span>
                              <span style={{ width: '110px', textAlign: 'center', flexShrink: 0 }}>
                                <span className={`badge ${row.inHub ? 'badge-active' : 'badge-inactive'}`} style={{ fontSize: '0.7rem' }}>
                                  {row.inHub ? 'Yes' : 'Missing'}
                                </span>
                              </span>
                              <span style={{ width: '110px', textAlign: 'center', flexShrink: 0 }}>
                                <span className={`badge ${row.inKeycloak ? 'badge-active' : 'badge-inactive'}`} style={{ fontSize: '0.7rem' }}>
                                  {row.inKeycloak ? 'Yes' : 'Missing'}
                                </span>
                              </span>
                            </div>
                          );
                        })}
                      </div>

                      <div style={{
                        display: 'flex',
                        gap: '1rem',
                        flexWrap: 'wrap',
                        marginTop: '0.375rem',
                        fontSize: '0.7rem',
                        color: 'var(--text-muted)',
                      }}>
                        <span>In both: {roleComparison.filter(r => r.inHub && r.inKeycloak).length}</span>
                        <span>TenantHub only: {roleComparison.filter(r => r.inHub && !r.inKeycloak).length}</span>
                        <span>Keycloak only: {roleComparison.filter(r => !r.inHub && r.inKeycloak).length}</span>
                        {isBulkKeycloakAction && <span><strong>Selected: {selectedSyncCodes.size}</strong></span>}
                      </div>
                      {keycloakErrors.selection && (
                        <div className="form-error-message">{keycloakErrors.selection}</div>
                      )}
                    </>
                  )}
                </div>
              )}

              {validationResult && (
                <div style={{
                  marginTop: '0.5rem',
                  padding: '0.75rem 1rem',
                  background: 'var(--bg-secondary)',
                  borderRadius: '6px',
                  border: '1px solid var(--border-color)',
                  fontSize: '0.8rem',
                }}>
                  <div style={{ fontWeight: 600, marginBottom: '0.5rem' }}>
                    Result: <span className={`badge ${validationResult.inSync ? 'badge-active' : 'badge-inactive'}`}>
                      {validationResult.status}
                    </span>
                  </div>
                  <div>Role type: {validationResult.roleType}</div>
                  <div>In database: {validationResult.existsInDatabase ? 'Yes' : 'No'}</div>
                  <div>In Keycloak: {validationResult.existsInKeycloak ? 'Yes' : 'No'}</div>
                </div>
              )}
            </div>

            <div className="modal-footer" style={{ minHeight: 'auto' }}>
              <button className="btn btn-secondary" onClick={() => setKeycloakDialogOpen(false)} disabled={keycloakBusy}>
                {validationResult ? 'Close' : 'Cancel'}
              </button>
              <button className="btn btn-primary" onClick={handleKeycloakSubmit} disabled={keycloakBusy}>
                {keycloakAction === 'import' ? 'Pull from Keycloak'
                  : keycloakAction === 'validate' ? 'Validate'
                    : keycloakAction === 'sync-all' ? 'Push to Keycloak'
                      : 'Sync'}
              </button>
            </div>
          </div>
        </>
      )}

      {/* Role Search Popup (F2) */}
      <SyutSearchGrid<RoleGridRow>
        isOpen={searchPopupOpen}
        onClose={() => setSearchPopupOpen(false)}
        onSelect={(role) => handleView(role)}
        title="Search Roles"
        columns={[
          { key: 'code', header: 'Code', width: '160px', sortable: true },
          { key: 'name', header: 'Name', width: '200px', sortable: true },
          { key: 'roleType', header: 'Type', width: '120px', sortable: true },
          {
            key: 'isComposite',
            header: 'Composite',
            width: '100px',
            render: (value: any) => (
              <span className={`badge ${value ? 'badge-active' : 'badge-inactive'}`} style={{ fontSize: '0.75rem' }}>
                {value ? 'Yes' : 'No'}
              </span>
            ),
          },
        ]}
        data={gridData}
        filters={[
          {
            key: 'roleType',
            label: 'Type',
            type: 'select',
            options: roleTypeOptions.filter(o => o.value).map(o => ({ value: o.value, label: o.label })),
          },
        ]}
        rowKey="id"
        enableRecentlyUsed
        recentlyUsedKey="role-master-search"
        searchPlaceholder="Search by code or name... (F2 to focus)"
      />
    </div>
  );
};

export default RoleMasterScreen;
